import { Dispatch, SetStateAction } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { CompanyDto } from 'tdc-web-backend/companies/schemas';
import { Box } from '@mui/material';
import { EnumTechLanguage } from 'tdc-web-backend/enums/enums';
import { useTranslation } from 'react-i18next';
import { ReactComponent as SuitcaseCheckIcon } from '../../../../../../../../../../assets/icons/layout-icons/SuitcaseCheckIcon.svg';
import ConfirmModal from '../../../../../../../../../../components/confirm-modal/ConfirmModal';
import { primaryLight } from '../../../../../../../../../../utils/color';
import CustomInput from '../../../../../../../../../../components/custom-input/CustomInput';
import { mapTechLanguage } from '../../../../../../../../../../utils/enums';
import { useRefresh } from '../../../../../../../../../../utils/hooks/crud-hooks/useRefresh';
import useUserCompany from '../../../../../../../../../../utils/hooks/useUserCompany';

interface CompanyTechLanguagesEditModalProps {
  isEditModalOpen: boolean;
  setIsEditModalOpen: Dispatch<SetStateAction<boolean>>;
}

const CompanyTechLanguagesEditModal = ({
  isEditModalOpen,
  setIsEditModalOpen,
}: CompanyTechLanguagesEditModalProps) => {
  const { company: companyData, updateCompany, isLoadingUpdateCompany } = useUserCompany();
  const refresh = useRefresh();
  const { t } = useTranslation();
  const formMethods = useForm<Pick<CompanyDto, 'techLanguages'>>({
    defaultValues: {
      techLanguages: companyData?.techLanguages ?? ([] as EnumTechLanguage[]),
    },
    mode: 'onChange',
  });

  const { handleSubmit, getValues } = formMethods;

  const onSubmit = () => {
    updateCompany(
      { techLanguages: getValues('techLanguages') },
      {
        onSuccess: () => {
          refresh();
          setIsEditModalOpen(false);
        },
      },
    );
  };

  return (
    <ConfirmModal
      width="40%"
      isModalOpen={isEditModalOpen}
      setIsModalOpen={setIsEditModalOpen}
      title={t('settings.companyProfile.programmingLanguages')}
      onCancel={() => setIsEditModalOpen(false)}
      automaticallyCloseModalOnButtonClick={false}
      onConfirm={handleSubmit(onSubmit)}
      confirmButtonText={t('common.save')}
      isLoading={isLoadingUpdateCompany}
      message={
        <FormProvider {...formMethods}>
          <Box component="form">
            <CustomInput
              input={<CustomInput.Autocomplete />}
              choices={mapTechLanguage}
              name="techLanguages"
              label={t('settings.companyProfile.programmingLanguages')}
              multiple
              placeholder={t('common.select')}
              sx={{ width: '100%' }}
            />
          </Box>
        </FormProvider>
      }
      iconProps={{
        icon: <SuitcaseCheckIcon fill={primaryLight[500]} />,
        wrapperBackgroundColor: primaryLight[100],
      }}
    />
  );
};

export default CompanyTechLanguagesEditModal;
